import { useLayoutEffect, useRef, useState } from "react";
import { EtapaIcon } from "./EtapaIcon";
import { Check } from "lucide-react";
import { formatFechaHora } from "@/lib/format";
import { RASTREO } from "@/constants/testIds";

const ESTILOS = {
  completada: {
    circle: "bg-[var(--tc-state-done)] text-white border-transparent",
    label: "text-[var(--tc-text)] font-medium",
  },
  actual: {
    circle: "bg-[var(--tc-accent)] text-white border-transparent ring-4 ring-[var(--tc-accent-soft)]",
    label: "text-[var(--tc-accent)] font-semibold",
  },
  pendiente: {
    circle: "bg-white text-[var(--tc-state-pending)] border-[var(--tc-border)]",
    label: "text-[var(--tc-state-pending)]",
  },
};

// Círculo: SIEMPRE el mismo tamaño (40px). La etapa actual se distingue por
// el anillo, el color de relleno y el peso de la tipografía — nunca por tamaño,
// para que las etiquetas de todas las etapas apoyen en la misma línea base.
const Circulo = ({ paso, size, innerRef }) => {
  const s = ESTILOS[paso.estado];
  return (
    <span
      ref={innerRef}
      className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors ${s.circle}`}
    >
      {paso.estado === "completada" ? (
        <Check size={18} strokeWidth={2.6} />
      ) : (
        <EtapaIcon nombre={paso.etapa.icono} size={size} strokeWidth={2.1} />
      )}
    </span>
  );
};

// Un solo componente para móvil y escritorio: la dirección cambia con CSS
// (flex-col → lg:flex-row). El conector es UNA línea de fondo continua con
// una línea de progreso pintada encima, medidas una sola vez a partir de la
// posición real (en píxeles) de los círculos — así nunca se desalinea ni se
// parte en dos colores, sin importar cuánto ocupe cada etiqueta.
export const Timeline = ({ timeline }) => {
  const contenedorRef = useRef(null);
  const circuloRefs = useRef([]);
  const [linea, setLinea] = useState(null);

  // Si ninguna etapa está "actual" (unidad sin eventos), NO asumimos que está
  // completa: el progreso llega, como máximo, hasta la última etapa "completada".
  // Si tampoco hay ninguna completada, no hay línea de progreso (idxProgreso = -1).
  const idxActual = timeline.findIndex((p) => p.estado === "actual");
  let idxProgreso = idxActual;
  if (idxProgreso === -1) {
    for (let i = timeline.length - 1; i >= 0; i--) {
      if (timeline[i].estado === "completada") { idxProgreso = i; break; }
    }
  }

  useLayoutEffect(() => {
    const medir = () => {
      // Recorta refs viejas de un timeline más largo montado antes: si no,
      // quedan nulls al final y la condición de abajo nunca mide la línea.
      circuloRefs.current.length = timeline.length;
      const cont = contenedorRef.current;
      const nodos = circuloRefs.current;
      if (!cont || nodos.length < 2 || nodos.some((n) => !n)) {
        setLinea(null);
        return;
      }
      const contRect = cont.getBoundingClientRect();
      const centros = nodos.map((n) => {
        const r = n.getBoundingClientRect();
        return { x: r.left + r.width / 2 - contRect.left, y: r.top + r.height / 2 - contRect.top };
      });
      const primero = centros[0];
      const ultimo = centros[centros.length - 1];
      const progreso = idxProgreso >= 0 ? centros[idxProgreso] : null;
      const horizontal = Math.abs(ultimo.x - primero.x) >= Math.abs(ultimo.y - primero.y);
      setLinea({ horizontal, primero, ultimo, progreso });
    };

    medir();
    const ro = new ResizeObserver(medir);
    if (contenedorRef.current) ro.observe(contenedorRef.current);
    window.addEventListener("resize", medir);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", medir);
    };
  }, [timeline, idxProgreso]);

  return (
    <section data-testid={RASTREO.timeline}>
      <div ref={contenedorRef} className="relative flex flex-col lg:flex-row">
        {linea && (
          <>
            <span
              aria-hidden="true"
              className="absolute bg-[var(--tc-border)]"
              style={
                linea.horizontal
                  ? { left: linea.primero.x, top: linea.primero.y - 1, width: linea.ultimo.x - linea.primero.x, height: 2 }
                  : { left: linea.primero.x - 1, top: linea.primero.y, width: 2, height: linea.ultimo.y - linea.primero.y }
              }
            />
            {linea.progreso && (
              <span
                aria-hidden="true"
                className="absolute bg-[var(--tc-state-done)] transition-all duration-300"
                style={
                  linea.horizontal
                    ? { left: linea.primero.x, top: linea.primero.y - 1, width: Math.max(0, linea.progreso.x - linea.primero.x), height: 2 }
                    : { left: linea.primero.x - 1, top: linea.primero.y, width: 2, height: Math.max(0, linea.progreso.y - linea.primero.y) }
                }
              />
            )}
          </>
        )}

        {timeline.map((paso, i) => {
          const s = ESTILOS[paso.estado];
          const esActual = paso.estado === "actual";
          return (
            <div
              key={paso.etapa.id}
              data-testid={RASTREO.timelineStep}
              data-estado={paso.estado}
              className="relative flex items-start gap-4 pb-7 last:pb-0 lg:flex-1 lg:flex-col lg:items-center lg:gap-0 lg:pb-0"
            >
              <Circulo paso={paso} size={18} innerRef={(el) => (circuloRefs.current[i] = el)} />
              <div
                className={`pt-1 transition-colors duration-150 lg:mt-3 lg:pt-0 lg:text-center ${
                  esActual
                    ? "-my-1.5 -ml-3 -mr-3 rounded-[var(--tc-radius)] border border-[var(--tc-timeline-current-border)] bg-[var(--tc-timeline-current-bg)] px-3 py-2 lg:mx-0 lg:my-0 lg:px-2.5 lg:py-1.5"
                    : "lg:px-2.5 lg:py-1.5"
                }`}
              >
                <p className={`text-[15px] lg:text-sm ${s.label}`}>{paso.etapa.nombre}</p>
                {paso.fecha ? (
                  <p className="mt-0.5 text-xs tabular-nums leading-tight text-[var(--tc-text-secondary)]">
                    {formatFechaHora(paso.fecha)}
                  </p>
                ) : (
                  <p className="mt-0.5 text-xs text-[var(--tc-state-pending)]">Pendiente</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
